import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api, setSessionLostHandler } from './api.js';
import { useLang } from './i18n.js';
import { Avatar, Toast, Spinner } from './ui.jsx';
import SignIn from './screens/signin.jsx';
import Today from './screens/today.jsx';
import Deals from './screens/deals.jsx';
import Clients from './screens/clients.jsx';
import ColdCall from './screens/coldcall.jsx';
import Accounts from './screens/accounts.jsx';
import Review from './screens/review.jsx';
import Insights from './screens/insights.jsx';
import Admin from './screens/admin.jsx';
import { DealDrawer, ClientDrawer } from './drawers.jsx';
import { NotificationBell } from './notifications.jsx';
import { QuickLog } from './modals.jsx';
import { CallForm } from './callform.jsx';
import { GraduateModal } from './graduate.jsx';

const ACCENTS = {
  // Raw brand colours fail contrast on a light background, so each accent
  // carries a light-mode twin. Same lesson Vendor OS learned.
  purple: { dark: '#9B4DFF', light: '#6F1BFF' },
  magenta: { dark: '#FF4DBD', light: '#E60E92' },
  cyan: { dark: '#73FDFF', light: '#007A8C' },
  coral: { dark: '#FF7A66', light: '#D8412A' },
};

const ROUTES = ['today', 'deals', 'calls', 'clients', 'accounts', 'upsells', 'review', 'insights', 'admin'];

const NAV = [
  { route: 'today', key: 'navToday', dot: 'var(--primary)' },
  { route: 'deals', key: 'navDeals', dot: 'var(--entity-btm)' },
  { route: 'calls', key: 'navCalls', dot: 'var(--warning)' },
  { route: 'clients', key: 'navClients', dot: 'var(--ink-3)' },
  { route: 'accounts', key: 'navAccounts', dot: 'var(--success)' },
  { route: 'upsells', key: 'navUpsells', dot: 'var(--entity-mr)' },
  { route: 'review', key: 'navReview', dot: 'var(--info)' },
  { route: 'insights', key: 'navInsights', dot: 'var(--ink-3)' },
  { route: 'admin', key: 'navAdmin', dot: 'var(--ink-3)', adminOnly: true },
];

const readRoute = () => {
  const r = window.location.hash.replace('#', '');
  return ROUTES.includes(r) ? r : 'today';
};

export default function App() {
  const { t, lang, setLang } = useLang();
  const [boot, setBoot] = useState(null);
  const [error, setError] = useState('');
  const [route, setRoute] = useState(readRoute);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dealId, setDealId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [quickLog, setQuickLog] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [graduating, setGraduating] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [toastMsg, setToastMsg] = useState('');
  const [signInNotice, setSignInNotice] = useState('');
  const toastTimer = useRef(null);

  const toast = useCallback((message) => {
    setToastMsg(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 3200);
  }, []);

  const load = useCallback(
    () =>
      api
        .bootstrap()
        .then((b) => {
          setBoot(b);
          const p = b.me?.prefs;
          applyPrefs({
            theme: p?.prefs_theme ?? b.config.default_theme,
            density: p?.prefs_density ?? b.config.default_density,
            accent: p?.prefs_accent ?? b.config.default_accent,
          });
          if (p?.prefs_lang) setLang(p.prefs_lang);
        })
        .catch((e) => setError(e.message)),
    [setLang],
  );

  useEffect(() => {
    setSessionLostHandler(() => setBoot((b) => (b ? { ...b, me: null } : b)));
    load();
  }, [load]);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // ⌘K / Ctrl-K opens quick capture from anywhere. Two-second logging was the
  // brief's actual ask; a keyboard route to it is the cheapest version of that.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setQuickLog({});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function applyPrefs({ theme, density, accent }) {
    const root = document.documentElement;
    if (theme) root.setAttribute('data-crm-theme', theme);
    if (density) root.setAttribute('data-density', density);
    const pair = ACCENTS[accent] || ACCENTS.purple;
    const resolvedTheme = theme || root.getAttribute('data-crm-theme');
    root.style.setProperty('--primary', resolvedTheme === 'dark' ? pair.dark : pair.light);
  }

  /** Optimistic: the theme flips instantly, then persists server-side so it
   *  follows the person to their phone rather than living in one browser. */
  async function savePref(key, value) {
    const map = { theme: 'prefs_theme', density: 'prefs_density', accent: 'prefs_accent' };
    setBoot((b) => ({ ...b, me: { ...b.me, prefs: { ...b.me.prefs, [map[key]]: value } } }));
    applyPrefs({ [key]: value });
    try {
      await api.savePrefs({ [map[key]]: value });
    } catch (e) {
      toast(e.message);
    }
  }

  async function changeLang(next) {
    setLang(next);
    try {
      await api.savePrefs({ prefs_lang: next });
    } catch {
      /* preference only — not worth interrupting anyone over */
    }
  }

  const go = (r) => {
    window.location.hash = r;
    setRoute(r);
    setMenuOpen(false);
  };

  /**
   * Remount the current screen after a write that changed data it displays.
   * Every screen fetches on mount, so bumping a key is a correct (if blunt)
   * reconciliation — and honest, unlike patching local state and hoping it
   * matches what the server actually did. Bootstrap reloads too, for the counts.
   */
  const refreshScreen = () => {
    setReloadKey((k) => k + 1);
    load();
  };

  if (error) {
    return (
      <div className="page">
        <p>{t('somethingWrong')}</p>
        <p className="dim">{error}</p>
        <button className="btn" onClick={() => window.location.reload()}>
          {t('retry')}
        </button>
      </div>
    );
  }
  if (!boot) return <Spinner label={t('loading')} />;

  if (!boot.me) {
    return (
      <SignIn
        googleClientId={boot.config.google_client_id}
        notice={signInNotice}
        /**
         * Confirm the session actually took before reloading.
         *
         * The old version reloaded unconditionally. When the server signed you
         * in correctly but the browser refused to keep the cookie, the reload
         * landed straight back on this screen with no message — identical to
         * the button doing nothing at all, and impossible to tell apart from a
         * failed sign-in. Re-reading /bootstrap first turns that silent case
         * into a sentence that names the real cause.
         */
        onSignedIn={async () => {
          const b = await api.bootstrap().catch(() => null);
          if (b?.me) {
            setBoot(b);
            const p = b.me.prefs;
            applyPrefs({
              theme: p?.prefs_theme ?? b.config.default_theme,
              density: p?.prefs_density ?? b.config.default_density,
              accent: p?.prefs_accent ?? b.config.default_accent,
            });
            if (p?.prefs_lang) setLang(p.prefs_lang);
          } else {
            setSignInNotice(t('sessionNotKept'));
          }
        }}
      />
    );
  }

  const me = boot.me;
  const nav = NAV.filter((n) => !n.adminOnly || me.role === 'admin');
  const prefs = me.prefs || {};

  const screen = () => {
    switch (route) {
      case 'today':
        return (
          <Today
            key={reloadKey}
            me={me}
            toast={toast}
            onOpenDeal={setDealId}
            onOpenClient={setClientId}
            onQuickLog={() => setQuickLog({})}
            onGraduate={setGraduating}
            onLogCall={setCallTarget}
          />
        );
      case 'calls':
        return (
          <ColdCall
            key={reloadKey}
            me={me}
            config={boot.config}
            toast={toast}
            onOpenClient={setClientId}
            onLogCall={setCallTarget}
          />
        );
      case 'accounts':
        return (
          <Accounts key={reloadKey} me={me} toast={toast} onOpenClient={setClientId} />
        );
      case 'deals':
        return (
          <Deals kind="new" config={boot.config} me={me} onOpenDeal={setDealId} toast={toast} />
        );
      case 'upsells':
        return (
          <Deals kind="upsell" config={boot.config} me={me} onOpenDeal={setDealId} toast={toast} />
        );
      case 'clients':
        return <Clients me={me} onOpenClient={setClientId} />;
      case 'review':
        return <Review key={reloadKey} me={me} toast={toast} />;
      case 'insights':
        return (
          <Insights
            key={reloadKey}
            me={me}
            onOpenDeal={setDealId}
            onOpenClient={setClientId}
          />
        );
      case 'admin':
        return <Admin key={reloadKey} me={me} toast={toast} />;
      // Unreachable: readRoute() only ever returns a member of ROUTES, and every
      // one of them is handled above. Kept so an added route fails visibly
      // rather than rendering the previous screen.
      default:
        return null;
    }
  };

  return (
    <div className="shell">
      {/* ------------------------------------------------------------ sidebar */}
      <nav className="sidebar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 18px' }}>
          <span
            style={{
              width: 13,
              height: 13,
              borderRadius: '50%',
              border: '1.5px dashed var(--entity-btm)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--entity-btm)' }}
            />
          </span>
          <span
            style={{
              width: 13,
              height: 13,
              borderRadius: '50%',
              border: '3px solid var(--entity-mr)',
            }}
          />
          <span style={{ fontFamily: 'var(--f-display)', fontWeight: 600, fontSize: 13 }}>CRM</span>
          <span
            style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600, marginLeft: 'auto' }}
          >
            v3
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {nav.map((n) => (
            <button
              key={n.route}
              className={`nav-item${route === n.route ? ' is-active' : ''}`}
              onClick={() => go(n.route)}
            >
              <span className="nav-dot" style={{ background: n.dot }} />
              {t(n.key)}
              {n.locked && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink-3)' }}>soon</span>
              )}
              {n.route === 'today' && boot.counts?.to_graduate > 0 && (
                <span className="nav-badge">{boot.counts.to_graduate}</span>
              )}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', borderTop: '1px solid var(--line)' }}>
          {menuOpen && (
            <UserMenu
              me={me}
              prefs={prefs}
              lang={lang}
              onLang={changeLang}
              onPref={savePref}
              onSignOut={() => api.signOut().then(() => window.location.reload())}
            />
          )}
          <div
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 10,
              cursor: 'pointer',
              borderRadius: 'var(--r-md)',
            }}
          >
            <Avatar initials={prefs.avatar_initials || me.name[0]} seed={prefs.avatar_var} size={30} />
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>{me.name}</span>
              <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)' }}>
                {me.role} · {me.entities.join(' + ') || '—'}
              </span>
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>⋯</span>
          </div>
        </div>
      </nav>

      {/* --------------------------------------------------------------- main */}
      <div className="main">
        <div className="topbar">
          <button className="search-trigger" onClick={() => setQuickLog({})}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <circle cx="5.5" cy="5.5" r="4" />
              <path d="M8.5 8.5 12 12" />
            </svg>
            {t('quickLog')}
            <span className="kbd">⌘K</span>
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <NotificationBell
              unread={boot.counts?.unread ?? 0}
              onOpenDeal={setDealId}
              onOpenClient={setClientId}
              onRoute={go}
              onChanged={load}
            />
            <button className="btn btn-primary" onClick={() => setQuickLog({})}>
              {t('logDeal')}
            </button>
          </div>
        </div>

        <div className="content">{screen()}</div>
      </div>

      {/* ------------------------------------------------------- mobile tabs */}
      <div className="tabbar">
        {nav.slice(0, 3).map((n) => (
          <button
            key={n.route}
            className={route === n.route ? 'is-on' : ''}
            onClick={() => go(n.route)}
          >
            <span className="nav-dot" style={{ background: n.dot }} />
            {t(n.key).split(' ')[0]}
          </button>
        ))}
        <button onClick={() => setQuickLog({})}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'var(--primary)',
              color: 'var(--on-primary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              lineHeight: 1,
            }}
          >
            +
          </span>
          {t('quickLog').split(' ')[0]}
        </button>
        <button
          className={route === 'clients' ? 'is-on' : ''}
          onClick={() => go('clients')}
        >
          <span className="nav-dot" style={{ background: 'var(--ink-3)' }} />
          {t('navClients')}
        </button>
      </div>

      {/* ------------------------------------------------------------ layers */}
      {dealId && (
        <DealDrawer
          id={dealId}
          onClose={() => setDealId(null)}
          onOpenClient={(id) => {
            setDealId(null);
            setClientId(id);
          }}
          onLog={(target) => setQuickLog(target)}
          toast={toast}
        />
      )}
      {clientId && (
        <ClientDrawer
          id={clientId}
          onClose={() => setClientId(null)}
          onOpenDeal={(id) => {
            setClientId(null);
            setDealId(id);
          }}
          onLog={(target) => setQuickLog(target)}
        />
      )}
      {quickLog && (
        <QuickLog
          target={quickLog}
          onClose={() => setQuickLog(null)}
          onDone={() => {
            setQuickLog(null);
            refreshScreen();
          }}
          toast={toast}
        />
      )}
      {callTarget && (
        <CallForm
          target={callTarget}
          config={boot.config}
          me={me}
          onClose={() => setCallTarget(null)}
          onSaved={() => {
            setCallTarget(null);
            refreshScreen();
          }}
          toast={toast}
        />
      )}
      {graduating && (
        <GraduateModal
          deal={graduating}
          config={boot.config}
          me={me}
          onClose={() => setGraduating(null)}
          onDone={() => {
            setGraduating(null);
            refreshScreen();
          }}
          toast={toast}
        />
      )}
      <Toast message={toastMsg} />
    </div>
  );
}

function UserMenu({ me, prefs, lang, onLang, onPref, onSignOut }) {
  const { t } = useLang();
  const Group = ({ label, children }) => (
    <>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
          padding: '6px 8px 4px',
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', gap: 5, padding: '0 8px 8px' }}>{children}</div>
    </>
  );
  const Opt = ({ on, onClick, children }) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        height: 30,
        borderRadius: 'var(--r-md)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        background: on ? 'var(--primary-soft)' : 'var(--surface)',
        color: on ? 'var(--primary)' : 'var(--ink-2)',
        border: `1px solid ${on ? 'var(--primary)' : 'var(--line)'}`,
      }}
    >
      {children}
    </button>
  );

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: 6,
        right: 6,
        background: 'var(--raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-3)',
        padding: 8,
        zIndex: 50,
      }}
    >
      <Group label={t('language')}>
        <Opt on={lang === 'en'} onClick={() => onLang('en')}>
          EN
        </Opt>
        <Opt on={lang === 'th'} onClick={() => onLang('th')}>
          ไทย
        </Opt>
      </Group>

      <Group label={t('appearance')}>
        <Opt on={prefs.prefs_theme === 'light'} onClick={() => onPref('theme', 'light')}>
          {t('light')}
        </Opt>
        <Opt on={prefs.prefs_theme === 'dark'} onClick={() => onPref('theme', 'dark')}>
          {t('dark')}
        </Opt>
      </Group>

      <Group label="">
        <Opt on={prefs.prefs_density === 'comfortable'} onClick={() => onPref('density', 'comfortable')}>
          {t('comfortable')}
        </Opt>
        <Opt on={prefs.prefs_density === 'compact'} onClick={() => onPref('density', 'compact')}>
          {t('compact')}
        </Opt>
      </Group>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px 10px' }}>
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', flex: 1 }}>{t('accent')}</span>
        {Object.entries(ACCENTS).map(([name, pair]) => (
          <button
            key={name}
            title={name}
            onClick={() => onPref('accent', name)}
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: prefs.prefs_theme === 'dark' ? pair.dark : pair.light,
              cursor: 'pointer',
              border: 0,
              boxShadow:
                prefs.prefs_accent === name
                  ? `0 0 0 2px var(--surface), 0 0 0 4px ${
                      prefs.prefs_theme === 'dark' ? pair.dark : pair.light
                    }`
                  : 'none',
            }}
          />
        ))}
      </div>

      <div style={{ height: 1, background: 'var(--line)', margin: '2px 0 6px' }} />
      <button className="nav-item" onClick={onSignOut}>
        ⏻ {t('signOut')}
      </button>
    </div>
  );
}
